import React, { useState } from "react";
import Layout from "../components/Layout";
import AdminNavBar from "../components/AdminNavBar";

import WholesaleCampaignForm from "../components/WholesaleCampaignForm";
import RetailCampaignForm from "../components/RetailCampaignForm";
import CommunityAdvocacyForm from "../components/CommunityAdvocacyForm";
import GlobalComplianceForm from "../components/GlobalComplianceForm";
import CampaignOutboxTab from "../components/campaignOutboxTab";

type CampaignType =
  | "WHOLESALE_PRODUCT_DROP"
  | "RETAIL_EDUCATION_UPDATE"
  | "COMMUNITY_ADVOCACY"
  | "GLOBAL_COMPLIANCE";

interface CampaignTypeConfig {
  id: CampaignType;
  label: string;
  description: string;
  defaultListIds: number[];
  targetListsLabel: string;
  primaryGoal: string;
}

const CAMPAIGN_TYPES: CampaignTypeConfig[] = [
  {
    id: "WHOLESALE_PRODUCT_DROP",
    label: "Wholesale Product Drop",
    description:
      "B2B drop announcements for dispensaries and wholesale buyers with batch + COA details.",
    defaultListIds: [4],
    targetListsLabel: "Distributors – Wholesale (ID 4)",
    primaryGoal:
      "Notify wholesale buyers about new or restocked SKUs so they can place orders.",
  },
  {
    id: "RETAIL_EDUCATION_UPDATE",
    label: "Retail Education / Farm Update",
    description:
      "21+ consumer-facing farm news and education. No pricing, no potency numbers.",
    defaultListIds: [5],
    targetListsLabel: "Retail Fans (opt-in 21+, ID 5)",
    primaryGoal:
      "Educate and build loyalty with 21+ fans using farm stories and safe, non-promotional product highlights.",
  },
  {
    id: "COMMUNITY_ADVOCACY",
    label: "Community & Advocacy",
    description:
      "Policy, community events, partnerships, and educational content for advocates and partners.",
    defaultListIds: [6],
    targetListsLabel: "Community & Advocacy (ID 6)",
    primaryGoal:
      "Share policy updates, community events, and advocacy news with partners and local supporters.",
  },
  {
    id: "GLOBAL_COMPLIANCE",
    label: "Global Compliance / Announcement",
    description:
      "Non-promotional announcements like policy changes, recalls, and legal/compliance notices.",
    defaultListIds: [7],
    targetListsLabel: "All Contacts (ID 7)",
    primaryGoal:
      "Send important non-marketing announcements to all contacts (compliance, recalls, policy changes).",
  },
];

export default function CreateUpdate() {
  // top-level tab: builder vs outbox
  const [activeTab, setActiveTab] = useState<"builder" | "outbox">("builder");

  // within builder: which campaign type
  const [selectedTypeId, setSelectedTypeId] =
    useState<CampaignType>("WHOLESALE_PRODUCT_DROP");

  const selectedType = CAMPAIGN_TYPES.find(
    (t) => t.id === selectedTypeId
  ) as CampaignTypeConfig;

  return (
    <Layout>
      <AdminNavBar />
      <div className="p-4 max-w-5xl mx-auto pt-[110px] space-y-6">
        <h2 className="text-2xl font-bold text-center">Manage Campaigns</h2>

        {/* Top-level tabs: Build vs Outbox */}
        <div className="flex justify-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("builder")}
            className={
              "px-4 py-2 rounded-full text-sm font-medium border " +
              (activeTab === "builder"
                ? "bg-green-600 text-white border-green-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100")
            }
          >
            Build Campaign
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("outbox")}
            className={
              "px-4 py-2 rounded-full text-sm font-medium border " +
              (activeTab === "outbox"
                ? "bg-green-600 text-white border-green-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100")
            }
          >
            Campaign Outbox
          </button>
        </div>

        {activeTab === "builder" && (
          <>
            {/* Campaign type selector */}
            <div className="flex flex-wrap gap-2 justify-center">
              {CAMPAIGN_TYPES.map((type) => {
                const isActive = type.id === selectedTypeId;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setSelectedTypeId(type.id)}
                    className={
                      "px-4 py-2 rounded-full text-sm font-medium border " +
                      (isActive
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100")
                    }
                  >
                    {type.label}
                  </button>
                );
              })}
            </div>

            {/* Selected type info */}
            <div className="border rounded-lg p-4 bg-white shadow-sm space-y-2">
              <h3 className="text-xl font-semibold">{selectedType.label}</h3>
              <p className="text-sm text-gray-700">
                {selectedType.description}
              </p>

              <div className="mt-2 text-sm space-y-1">
                <p>
                  <span className="font-semibold">Default target list(s): </span>
                  {selectedType.targetListsLabel}
                </p>
                <p>
                  <span className="font-semibold">Primary goal: </span>
                  {selectedType.primaryGoal}
                </p>
              </div>

              <div className="mt-3 text-xs text-gray-500 italic">
                Use the builder below to compose the email for this campaign
                type. It will be created as a draft in Listmonk using the
                Ridgetop base template.
              </div>
            </div>

            {/* Campaign-specific form */}
            {selectedTypeId === "WHOLESALE_PRODUCT_DROP" && (
              <WholesaleCampaignForm />
            )}
            {selectedTypeId === "RETAIL_EDUCATION_UPDATE" && (
              <RetailCampaignForm />
            )}
            {selectedTypeId === "COMMUNITY_ADVOCACY" && (
              <CommunityAdvocacyForm />
            )}
            {selectedTypeId === "GLOBAL_COMPLIANCE" && (
              <GlobalComplianceForm />
            )}
          </>
        )}

        {activeTab === "outbox" && <CampaignOutboxTab />}
      </div>
    </Layout>
  );
}
